import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { AdminPanelButton } from './AdminPanelButton'
import { Label } from '../../../components/ui/label'
import {
  Download,
  FileSpreadsheet,
  UserX,
  AlertCircle,
  Save,
  RefreshCw,
  Search,
  Plus,
  X,
  Calendar,
  CheckCircle2,
  ClipboardPaste,
  Users,
} from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'

type SearchUser = {
  id: string
  email: string
  username: string
  firstName?: string
  lastName?: string
}

const isoToday = () => new Date().toISOString().slice(0, 10)

const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

const normalizeId = (v: string) => v.trim().toLowerCase()

const parseBulkIdentities = (raw: string): string[] => {
  const parts = raw
    .split(/[\n,;]+/)
    .map((p) => normalizeId(p))
    .filter(Boolean)
  return [...new Set(parts)]
}

const displayName = (u: SearchUser) => {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return name || u.username || u.email
}

const looksLikeEmail = (id: string) => id.includes('@')

export const ReportsSection = () => {
  const [generating, setGenerating] = useState(false)
  const [loadingList, setLoadingList] = useState(true)
  const [saving, setSaving] = useState(false)
  const [excluded, setExcluded] = useState<string[]>([])
  const [savedCount, setSavedCount] = useState(0)
  const [dirty, setDirty] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [manualAdd, setManualAdd] = useState('')
  const [bulkPaste, setBulkPaste] = useState('')
  const [showBulk, setShowBulk] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [userQuery, setUserQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchUser[]>([])
  const [searching, setSearching] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadExcluded = useCallback(async () => {
    setLoadingList(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE_URL}admin/report/excluded-identities`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to load excluded users')
      const data = await res.json()
      const list: string[] = Array.isArray(data.identities) ? data.identities : []
      setExcluded(list)
      setSavedCount(list.length)
      setDirty(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load excluded users')
    } finally {
      setLoadingList(false)
    }
  }, [])

  useEffect(() => {
    void loadExcluded()
  }, [loadExcluded])

  useEffect(() => {
    const q = userQuery.trim()
    if (q.length < 2) {
      setSearchResults([])
      return
    }
    const t = setTimeout(async () => {
      setSearching(true)
      try {
        const params = new URLSearchParams({ search: q, page: '1', per_page: '12' })
        const res = await fetch(`${API_BASE_URL}admin/users?${params}`, {
          credentials: 'include',
        })
        if (!res.ok) return
        const data = await res.json()
        setSearchResults(Array.isArray(data.users) ? data.users : [])
      } catch {
        // ignore search errors
      } finally {
        setSearching(false)
      }
    }, 280)
    return () => clearTimeout(t)
  }, [userQuery])

  const listStats = useMemo(() => {
    const emails = excluded.filter(looksLikeEmail).length
    const usernames = excluded.length - emails
    return { total: excluded.length, emails, usernames }
  }, [excluded])

  const visibleExcluded = useMemo(() => {
    const f = filterText.trim().toLowerCase()
    if (!f) return excluded
    return excluded.filter((x) => x.includes(f))
  }, [excluded, filterText])

  const addIdentities = (rawList: string[]) => {
    const cleaned = rawList.map(normalizeId).filter(Boolean)
    if (!cleaned.length) return 0
    const existing = new Set(excluded)
    const uniqueNew = cleaned.filter((id) => !existing.has(id))
    if (!uniqueNew.length) return 0
    setExcluded((prev) => [...new Set([...prev, ...uniqueNew])].sort())
    setDirty(true)
    setMessage(null)
    setError(null)
    return uniqueNew.length
  }

  const addIdentity = (raw: string) => {
    const n = addIdentities([raw])
    if (n === 0 && normalizeId(raw)) {
      setMessage('Already on the exclude list')
    }
  }

  const removeIdentity = (id: string) => {
    setExcluded((prev) => prev.filter((x) => x !== id))
    setDirty(true)
    setMessage(null)
  }

  const removeVisible = () => {
    if (!visibleExcluded.length) return
    const removeSet = new Set(visibleExcluded)
    setExcluded((prev) => prev.filter((x) => !removeSet.has(x)))
    setDirty(true)
    setMessage(`Removed ${visibleExcluded.length} from list (save to persist)`)
  }

  const addUserToExclude = (u: SearchUser) => {
    const email = normalizeId(u.email || '')
    const username = normalizeId(u.username || '')
    const toAdd = [email, username].filter(Boolean)
    const added = addIdentities(toAdd)
    setUserQuery('')
    setSearchResults([])
    setMessage(
      added > 0
        ? `Added ${u.email || u.username} to exclude list (save to persist)`
        : `${u.email || u.username} already excluded`,
    )
  }

  const addAllSearchResults = () => {
    const toAdd: string[] = []
    for (const u of searchResults) {
      const email = normalizeId(u.email || '')
      const username = normalizeId(u.username || '')
      if (email) toAdd.push(email)
      if (username) toAdd.push(username)
    }
    const added = addIdentities(toAdd)
    setMessage(
      added > 0
        ? `Added ${added} identities from search (save to persist)`
        : 'All search results already excluded',
    )
  }

  const applyBulkPaste = () => {
    const parsed = parseBulkIdentities(bulkPaste)
    if (!parsed.length) {
      setError('Paste emails or usernames separated by commas, semicolons, or new lines')
      return
    }
    const added = addIdentities(parsed)
    setBulkPaste('')
    setShowBulk(false)
    setMessage(
      added > 0
        ? `Added ${added} of ${parsed.length} pasted identities (save to persist)`
        : 'All pasted identities were already excluded',
    )
  }

  const saveExcluded = async () => {
    setSaving(true)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch(`${API_BASE_URL}admin/report/excluded-identities`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identities: excluded }),
      })
      if (!res.ok) throw new Error('Failed to save excluded users')
      const data = await res.json()
      const list: string[] = Array.isArray(data.identities) ? data.identities : []
      setExcluded(list)
      setSavedCount(list.length)
      setDirty(false)
      setMessage(`Saved ${list.length} excluded identities`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const applyPreset = (preset: '7d' | '30d' | '90d' | 'clear') => {
    if (preset === 'clear') {
      setDateFrom('')
      setDateTo('')
      return
    }
    const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90
    setDateFrom(daysAgo(days))
    setDateTo(isoToday())
  }

  const creditMonthHint = useMemo(() => {
    const end = dateTo || isoToday()
    return end.slice(0, 7)
  }, [dateTo])

  const downloadExcel = async () => {
    if (dirty) {
      setError('Save the excluded list before generating the report')
      return
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setError('From date must be on or before To date')
      return
    }
    setGenerating(true)
    setMessage(null)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (dateFrom) params.set('date_from', dateFrom)
      if (dateTo) params.set('date_to', dateTo)
      const qs = params.toString()
      const res = await fetch(`${API_BASE_URL}admin/report/usage-excel${qs ? `?${qs}` : ''}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (!res.ok) {
        let detail = 'Failed to generate Excel report'
        try {
          const err = await res.json()
          if (err?.detail) detail = String(err.detail)
        } catch {
          // ignore
        }
        throw new Error(detail)
      }
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') || ''
      const match = /filename="?([^"]+)"?/i.exec(disposition)
      const filename = match?.[1] || `Athena Usage Report.xlsx`
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setMessage(`Downloaded ${filename}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed')
    } finally {
      setGenerating(false)
    }
  }

  const fieldStyle: CSSProperties = {
    width: '100%',
    borderRadius: 10,
    border: '1px solid var(--bd)',
    background: 'var(--white)',
    padding: '10px 12px',
    fontSize: 13,
    color: 'var(--text)',
    outline: 'none',
  }

  const isAlreadyExcluded = (u: SearchUser) =>
    excluded.includes(normalizeId(u.email || '')) ||
    excluded.includes(normalizeId(u.username || ''))

  const searchableNotExcluded = searchResults.filter((u) => !isAlreadyExcluded(u))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {(message || error) && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            padding: '12px 14px',
            borderRadius: 12,
            border: `1px solid ${error ? 'rgba(185,28,28,.25)' : 'rgba(37,99,235,.25)'}`,
            background: error ? 'rgba(254,226,226,.55)' : 'rgba(219,234,254,.55)',
            color: error ? '#991b1b' : '#1e3a8a',
            fontSize: 13,
          }}
        >
          {error ? <AlertCircle size={16} style={{ marginTop: 2, flexShrink: 0 }} /> : <CheckCircle2 size={16} style={{ marginTop: 2, flexShrink: 0 }} />}
          <span>{error || message}</span>
        </div>
      )}

      {dirty && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            padding: '12px 14px',
            borderRadius: 12,
            border: '1px solid rgba(180,83,9,.35)',
            background: 'rgba(254,243,199,.7)',
            color: '#92400e',
            fontSize: 13,
            position: 'sticky',
            top: 8,
            zIndex: 5,
          }}
        >
          <span>Excluded list has unsaved changes — save before generating the report.</span>
          <AdminPanelButton type="button" variant="primary" onClick={() => void saveExcluded()} disabled={saving}>
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save now'}
          </AdminPanelButton>
        </div>
      )}

      {/* Generate report */}
      <Card className="overflow-hidden border-[var(--bd)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[15px]">
            <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
            Athena Usage Report
          </CardTitle>
          <CardDescription>
            Excel with adoption (Assigned vs Active), usage tabs, and credit system sheets (who is using how much,
            at/near limit). Excluded accounts are removed from every calculation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Calendar size={15} style={{ color: 'var(--muted)' }} />
              <Label className="text-[13px] font-medium">Date range</Label>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {(
                [
                  ['7d', 'Last 7 days'],
                  ['30d', 'Last 30 days'],
                  ['90d', 'Last 90 days'],
                  ['clear', 'All time'],
                ] as const
              ).map(([key, label]) => (
                <AdminPanelButton key={key} type="button" size="sm" onClick={() => applyPreset(key)}>
                  {label}
                </AdminPanelButton>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              <div>
                <Label htmlFor="report-from" className="text-xs text-muted-foreground mb-1.5 block">
                  From
                </Label>
                <input
                  id="report-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={fieldStyle}
                />
              </div>
              <div>
                <Label htmlFor="report-to" className="text-xs text-muted-foreground mb-1.5 block">
                  To
                </Label>
                <input
                  id="report-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={fieldStyle}
                />
              </div>
            </div>
            <p style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
              {dateFrom || dateTo
                ? `Usage window: ${dateFrom || '…'} → ${dateTo || '…'}. Credit sheet uses IST billing month ${creditMonthHint}.`
                : `No dates selected — usage uses 7d / 30d / 90d / All Time. Credit sheet uses current IST month (${creditMonthHint}).`}
            </p>
          </div>

          <AdminPanelButton
            type="button"
            variant="primary"
            onClick={() => void downloadExcel()}
            disabled={generating || dirty}
            style={{ minWidth: 220 }}
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                Generating Excel…
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Generate Excel report
              </>
            )}
          </AdminPanelButton>
        </CardContent>
      </Card>

      {/* Exclusions */}
      <Card className="overflow-hidden border-[var(--bd)] shadow-sm">
        <CardHeader className="pb-3">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <CardTitle className="flex items-center gap-2 text-[15px]">
                <UserX className="h-5 w-5 text-indigo-600" />
                Excluded users
              </CardTitle>
              <CardDescription>
                Search, paste, or type identities. Saved: {savedCount}
                {dirty ? ' · unsaved changes' : ''}
              </CardDescription>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <AdminPanelButton type="button" onClick={() => void loadExcluded()} disabled={loadingList}>
                <RefreshCw className="h-4 w-4" />
                Reload
              </AdminPanelButton>
              <AdminPanelButton
                type="button"
                variant="primary"
                onClick={() => void saveExcluded()}
                disabled={saving || loadingList || !dirty}
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving…' : 'Save list'}
              </AdminPanelButton>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 10,
            }}
          >
            {[
              { label: 'Total', value: listStats.total },
              { label: 'Emails', value: listStats.emails },
              { label: 'Usernames', value: listStats.usernames },
              { label: 'Saved on server', value: savedCount },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid var(--bd)',
                  background: 'rgba(27,42,74,.02)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.label}</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--navy)', marginTop: 2 }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Search users */}
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <Label className="text-xs text-muted-foreground">Search users to exclude</Label>
              {searchResults.length > 0 && searchableNotExcluded.length > 0 && (
                <AdminPanelButton type="button" size="sm" onClick={addAllSearchResults}>
                  <Users className="h-3.5 w-3.5" />
                  Add all ({searchableNotExcluded.length})
                </AdminPanelButton>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <Search
                size={15}
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted)',
                }}
              />
              <input
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Type name, email, or username…"
                style={{ ...fieldStyle, paddingLeft: 34 }}
              />
            </div>
            {(searching || searchResults.length > 0) && userQuery.trim().length >= 2 && (
              <div
                style={{
                  marginTop: 6,
                  border: '1px solid var(--bd)',
                  borderRadius: 12,
                  background: 'var(--white)',
                  boxShadow: 'var(--sh-sm)',
                  maxHeight: 280,
                  overflowY: 'auto',
                }}
              >
                {searching && (
                  <div style={{ padding: 12, fontSize: 12, color: 'var(--muted)' }}>Searching…</div>
                )}
                {!searching &&
                  searchResults.map((u) => {
                    const already = isAlreadyExcluded(u)
                    return (
                      <div
                        key={u.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          padding: '10px 12px',
                          borderBottom: '1px solid var(--bd)',
                          opacity: already ? 0.65 : 1,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)' }}>
                            {displayName(u)}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {u.email}
                            {u.username ? ` · ${u.username}` : ''}
                          </div>
                        </div>
                        <AdminPanelButton
                          type="button"
                          size="sm"
                          disabled={already}
                          onClick={() => addUserToExclude(u)}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {already ? 'Added' : 'Add'}
                        </AdminPanelButton>
                      </div>
                    )
                  })}
                {!searching && searchResults.length === 0 && (
                  <div style={{ padding: 12, fontSize: 12, color: 'var(--muted)' }}>No users found</div>
                )}
              </div>
            )}
          </div>

          {/* Manual add */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Add email / username</Label>
              <input
                value={manualAdd}
                onChange={(e) => setManualAdd(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addIdentity(manualAdd)
                    setManualAdd('')
                  }
                }}
                placeholder="name@example.com"
                style={fieldStyle}
              />
            </div>
            <AdminPanelButton
              type="button"
              onClick={() => {
                addIdentity(manualAdd)
                setManualAdd('')
              }}
            >
              <Plus className="h-4 w-4" />
              Add
            </AdminPanelButton>
            <AdminPanelButton type="button" onClick={() => setShowBulk((v) => !v)}>
              <ClipboardPaste className="h-4 w-4" />
              {showBulk ? 'Hide paste' : 'Bulk paste'}
            </AdminPanelButton>
          </div>

          {showBulk && (
            <div
              style={{
                padding: 12,
                borderRadius: 12,
                border: '1px solid var(--bd)',
                background: 'rgba(27,42,74,.02)',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <Label className="text-xs text-muted-foreground">
                Paste many emails/usernames (comma, semicolon, or one per line)
              </Label>
              <textarea
                value={bulkPaste}
                onChange={(e) => setBulkPaste(e.target.value)}
                rows={5}
                placeholder={'alice@example.com\nbob@example.com\ninternal-tester'}
                style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <AdminPanelButton type="button" variant="primary" onClick={applyBulkPaste}>
                  <Plus className="h-4 w-4" />
                  Add pasted
                </AdminPanelButton>
                <AdminPanelButton type="button" onClick={() => setBulkPaste('')}>
                  Clear
                </AdminPanelButton>
              </div>
            </div>
          )}

          {/* Filter + chips */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Label className="text-xs text-muted-foreground">
                Excluded list ({visibleExcluded.length}
                {filterText ? ` of ${excluded.length}` : ''})
              </Label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Filter list…"
                  style={{ ...fieldStyle, maxWidth: 200, padding: '7px 10px', fontSize: 12 }}
                />
                {filterText && (
                  <AdminPanelButton type="button" size="sm" onClick={() => setFilterText('')}>
                    Clear filter
                  </AdminPanelButton>
                )}
                {visibleExcluded.length > 0 && (
                  <AdminPanelButton type="button" size="sm" onClick={removeVisible}>
                    <X className="h-3.5 w-3.5" />
                    Remove {filterText ? 'filtered' : 'all'}
                  </AdminPanelButton>
                )}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                maxHeight: 320,
                overflowY: 'auto',
                padding: 12,
                borderRadius: 12,
                border: '1px solid var(--bd)',
                background: 'rgba(27,42,74,.02)',
              }}
            >
              {loadingList && (
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Loading…</span>
              )}
              {!loadingList && visibleExcluded.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {filterText ? 'No matches in exclude list' : 'No excluded identities yet — search or paste to add'}
                </span>
              )}
              {visibleExcluded.map((id) => (
                <span
                  key={id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 8px 5px 10px',
                    borderRadius: 999,
                    background: 'var(--white)',
                    border: `1px solid ${looksLikeEmail(id) ? 'rgba(37,99,235,.25)' : 'var(--bd)'}`,
                    fontSize: 12,
                    color: 'var(--navy)',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                  title={looksLikeEmail(id) ? 'Email' : 'Username'}
                >
                  {id}
                  <button
                    type="button"
                    onClick={() => removeIdentity(id)}
                    title="Remove"
                    style={{
                      display: 'inline-flex',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      padding: 0,
                      color: 'var(--muted)',
                    }}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
